/**
 * 방문자 **유입 경로** 판별 · 보관.
 *
 * Amplitude SDK 의 web attribution 이 utm_*, referrer, 광고 클릭 ID 를 이미 user property
 * (`utm_source` / `initial_utm_source` …)로 남긴다. 여기서는 SDK 가 하지 않는 것만 보완한다.
 *
 *  - `ref` 쿼리 (파트너·QR·명함 링크 등에 붙이는 자체 식별자)
 *  - 유입 **채널** 분류 — 검색 / SNS / 광고 / 이메일 / 추천 사이트 / 직접 유입
 *  - 처음 도착한 페이지 경로 (landing page)
 *  - 문의 폼으로 넘길 "마지막 비직접(last non-direct) 유입" 값
 *
 * 저장 위치 (모두 이 기기 브라우저에만 남는다 — 서버로 보내는 건 Amplitude 이벤트뿐)
 *  - first touch   : localStorage — 최초 1회만 기록
 *  - last touch    : localStorage — 직접 유입이 아닐 때마다 덮어씀 (GA 의 last non-direct 와 같은 규칙)
 *  - session touch : sessionStorage — 이번 방문(탭 세션)에 들어온 경로
 *
 * ⚠️ 여기에는 개인정보가 들어가지 않는다. referrer 는 **도메인만** 남기고 경로·쿼리는 버린다
 * (검색어나 다른 사이트의 토큰이 경로에 실려 오는 경우가 있다).
 */

export type TrafficChannel =
  | 'paid_search'
  | 'paid_social'
  | 'paid_other'
  | 'organic_search'
  | 'social'
  | 'email'
  | 'referral'
  | 'campaign'
  | 'direct'

export interface Touch {
  channel: TrafficChannel
  /** utm_source, 없으면 referrer 로 추정한 출처 (google, naver, linkedin, example.com, (direct)) */
  source: string
  /** utm_medium, 없으면 채널로 추정 (organic, social, referral, (none)) */
  medium: string
  campaign?: string
  term?: string
  content?: string
  /** `?ref=` 값 */
  ref?: string
  /** referrer 의 호스트명 (경로·쿼리는 저장하지 않음) */
  referring_domain?: string
  /** 광고 클릭 ID 중 붙어 온 것의 **이름** (값은 저장하지 않는다 — 예: "gclid") */
  click_id_type?: string
  /** 처음 도착한 경로 (쿼리 제외, 예: "/", "/viewer/abc") */
  landing_page: string
  /** utm_* 가 실제로 붙어 왔는지 (false 면 source/medium 은 추정값) */
  has_utm: boolean
  /** ISO 시각 */
  at: string
}

/** 사이트 자신으로 취급할 도메인 — 이 도메인에서 넘어온 referrer 는 유입으로 보지 않는다 */
const INTERNAL_DOMAINS = ['annx.ai']

/** 클릭 ID 파라미터 → 채널. fbclid 는 광고가 아니어도 페이스북이 모든 외부 링크에 붙이므로 social 로 본다. */
const CLICK_IDS: Record<string, { channel: TrafficChannel; source: string }> = {
  gclid: { channel: 'paid_search', source: 'google' },
  gbraid: { channel: 'paid_search', source: 'google' },
  wbraid: { channel: 'paid_search', source: 'google' },
  dclid: { channel: 'paid_other', source: 'google' },
  msclkid: { channel: 'paid_search', source: 'bing' },
  ttclid: { channel: 'paid_social', source: 'tiktok' },
  li_fat_id: { channel: 'paid_social', source: 'linkedin' },
  twclid: { channel: 'paid_social', source: 'x' },
  rdt_cid: { channel: 'paid_social', source: 'reddit' },
  ko_click_id: { channel: 'paid_other', source: 'kakao' },
  fbclid: { channel: 'social', source: 'facebook' },
}

/** [호스트 패턴, 출처 이름]. 앞쪽이 우선. */
const SEARCH_ENGINES: [RegExp, string][] = [
  [/(^|\.)search\.naver\.com$/, 'naver'],
  [/(^|\.)m\.search\.naver\.com$/, 'naver'],
  [/(^|\.)google\.[a-z.]+$/, 'google'],
  [/(^|\.)bing\.com$/, 'bing'],
  [/(^|\.)search\.daum\.net$/, 'daum'],
  [/(^|\.)daum\.net$/, 'daum'],
  [/(^|\.)search\.yahoo\.[a-z.]+$/, 'yahoo'],
  [/(^|\.)yahoo\.[a-z.]+$/, 'yahoo'],
  [/(^|\.)duckduckgo\.com$/, 'duckduckgo'],
  [/(^|\.)baidu\.com$/, 'baidu'],
  [/(^|\.)yandex\.[a-z.]+$/, 'yandex'],
  [/(^|\.)ecosia\.org$/, 'ecosia'],
  [/(^|\.)zum\.com$/, 'zum'],
  [/(^|\.)nate\.com$/, 'nate'],
  [/(^|\.)perplexity\.ai$/, 'perplexity'],
  [/(^|\.)chatgpt\.com$/, 'chatgpt'],
  [/(^|\.)naver\.com$/, 'naver'],
]

const SOCIAL_SITES: [RegExp, string][] = [
  [/(^|\.)(facebook\.com|fb\.com|fb\.me)$/, 'facebook'],
  [/(^|\.)instagram\.com$/, 'instagram'],
  [/(^|\.)(linkedin\.com|lnkd\.in)$/, 'linkedin'],
  [/(^|\.)threads\.(net|com)$/, 'threads'],
  [/(^|\.)(t\.co|twitter\.com|x\.com)$/, 'x'],
  [/(^|\.)(youtube\.com|youtu\.be)$/, 'youtube'],
  [/(^|\.)(kakao\.com|kakaocdn\.net|kakaotalk\.com)$/, 'kakao'],
  [/(^|\.)band\.us$/, 'band'],
  [/(^|\.)(blog|cafe|m\.blog|m\.cafe)\.naver\.com$/, 'naver_blog'],
  [/(^|\.)(cafe|blog)\.daum\.net$/, 'daum_cafe'],
  [/(^|\.)tistory\.com$/, 'tistory'],
  [/(^|\.)brunch\.co\.kr$/, 'brunch'],
  [/(^|\.)reddit\.com$/, 'reddit'],
  [/(^|\.)tiktok\.com$/, 'tiktok'],
  [/(^|\.)pinterest\.[a-z.]+$/, 'pinterest'],
  [/(^|\.)discord\.(com|gg)$/, 'discord'],
  [/(^|\.)slack\.com$/, 'slack'],
]

const WEBMAIL_SITES: [RegExp, string][] = [
  [/(^|\.)mail\.google\.com$/, 'gmail'],
  [/(^|\.)mail\.naver\.com$/, 'naver_mail'],
  [/(^|\.)mail\.daum\.net$/, 'daum_mail'],
  [/(^|\.)(outlook\.live\.com|outlook\.office\.com|outlook\.office365\.com)$/, 'outlook'],
]

const PAID_MEDIUMS = /^(cpc|ppc|cpm|cpv|cpa|paid|paidsearch|paid_search|paid-search|display|banner|ads?)$/i
const PAID_SOCIAL_MEDIUMS = /^(paid_?social|paid-social|social_?paid|sns_?ad|social-ad)$/i
const SOCIAL_MEDIUMS = /^(social|sns|social[-_]network|social[-_]media|sm|organic_?social)$/i
const EMAIL_MEDIUMS = /^(e-?mail|newsletter|edm)$/i
const SEARCH_MEDIUMS = /^(organic|seo)$/i
const REFERRAL_MEDIUMS = /^(referral|partner|affiliate|qr|offline|print)$/i

const KEY_FIRST = 'annx_touch_first'
const KEY_LAST = 'annx_touch_last'
const KEY_SESSION = 'annx_touch_session'

const matchHost = (host: string, table: [RegExp, string][]) =>
  table.find(([re]) => re.test(host))?.[1]

export const isInternalHost = (host: string) =>
  host === location.hostname ||
  INTERNAL_DOMAINS.some((d) => host === d || host.endsWith(`.${d}`))

const clean = (v: string | null) => {
  const s = v?.trim()
  // 지나치게 긴 값은 잘라서 저장 (이벤트 속성 크기 보호)
  return s ? s.slice(0, 200) : undefined
}

function classifyByMedium(medium: string, fallback: TrafficChannel): TrafficChannel {
  if (PAID_SOCIAL_MEDIUMS.test(medium)) return 'paid_social'
  if (PAID_MEDIUMS.test(medium)) return 'paid_search'
  if (SOCIAL_MEDIUMS.test(medium)) return 'social'
  if (EMAIL_MEDIUMS.test(medium)) return 'email'
  if (SEARCH_MEDIUMS.test(medium)) return 'organic_search'
  if (REFERRAL_MEDIUMS.test(medium)) return 'referral'
  return fallback
}

const DEFAULT_MEDIUM: Record<TrafficChannel, string> = {
  paid_search: 'cpc',
  paid_social: 'paid_social',
  paid_other: 'paid',
  organic_search: 'organic',
  social: 'social',
  email: 'email',
  referral: 'referral',
  campaign: '(not set)',
  direct: '(none)',
}

/**
 * 현재 주소와 referrer 로 이번 진입의 유입 정보를 만든다.
 * 사이트 내부 이동(같은 도메인 referrer)이고 캠페인 파라미터도 없으면 null — "새 유입이 아님".
 */
export function readCurrentTouch(
  url: URL = new URL(location.href),
  referrer: string = document.referrer,
): Touch | null {
  const q = url.searchParams
  const utm = {
    source: clean(q.get('utm_source')),
    medium: clean(q.get('utm_medium')),
    campaign: clean(q.get('utm_campaign')),
    term: clean(q.get('utm_term')),
    content: clean(q.get('utm_content')),
  }
  const ref = clean(q.get('ref'))
  const clickIdType = Object.keys(CLICK_IDS).find((k) => q.has(k))

  let referringDomain: string | undefined
  try {
    referringDomain = referrer ? new URL(referrer).hostname.replace(/^www\./, '') : undefined
  } catch {
    referringDomain = undefined
  }
  const internalReferrer = !!referringDomain && isInternalHost(referringDomain)
  if (internalReferrer) referringDomain = undefined

  const hasUtm = Object.values(utm).some(Boolean)
  const hasCampaign = hasUtm || !!ref || !!clickIdType

  // 내부 이동 — 새 유입으로 치지 않는다
  if (internalReferrer && !hasCampaign) return null

  // 웹메일·SNS 를 검색엔진보다 먼저 본다 (mail.naver.com, blog.naver.com 이 "naver 검색"으로 잡히지 않게)
  const mailSource = referringDomain && matchHost(referringDomain, WEBMAIL_SITES)
  const socialSource = !mailSource && referringDomain && matchHost(referringDomain, SOCIAL_SITES)
  const searchSource =
    !mailSource && !socialSource && referringDomain && matchHost(referringDomain, SEARCH_ENGINES)

  // 1) referrer 만으로 판단한 채널
  let channel: TrafficChannel = mailSource
    ? 'email'
    : socialSource
      ? 'social'
      : searchSource
        ? 'organic_search'
        : referringDomain
          ? 'referral'
          : 'direct'
  let source = mailSource || socialSource || searchSource || referringDomain || '(direct)'

  // 2) 클릭 ID 가 있으면 우선 (광고 클릭)
  if (clickIdType) {
    channel = CLICK_IDS[clickIdType].channel
    source = CLICK_IDS[clickIdType].source
  }

  // 3) ref 만 있고 referrer 가 없으면 (QR·메신저 등) 추천 유입으로 본다
  if (!hasUtm && !clickIdType && ref && channel === 'direct') {
    channel = 'referral'
    source = ref
  }

  // 4) utm 이 있으면 그것이 최우선 — 마케터가 명시한 값
  if (hasUtm) {
    if (utm.source) source = utm.source
    if (utm.medium) channel = classifyByMedium(utm.medium, 'campaign')
    else if (channel === 'direct') channel = 'campaign'
  }

  return {
    channel,
    source,
    medium: utm.medium ?? DEFAULT_MEDIUM[channel],
    campaign: utm.campaign,
    term: utm.term,
    content: utm.content,
    ref,
    referring_domain: referringDomain,
    click_id_type: clickIdType,
    landing_page: url.pathname,
    has_utm: hasUtm,
    at: new Date().toISOString(),
  }
}

/* ── 저장소 (사생활 보호 모드 등에서 막혀도 조용히 넘어간다) ── */

const read = (store: () => Storage, key: string): Touch | null => {
  try {
    const raw = store().getItem(key)
    return raw ? (JSON.parse(raw) as Touch) : null
  } catch {
    return null
  }
}
const write = (store: () => Storage, key: string, v: Touch) => {
  try {
    store().setItem(key, JSON.stringify(v))
  } catch {
    /* 저장 불가 — 메모리 값으로만 동작 */
  }
}
const local = () => window.localStorage
const session = () => window.sessionStorage

export interface TrafficState {
  first: Touch
  last: Touch
  session: Touch
  /** 이번 페이지 로드가 새 유입(새 세션 시작 또는 새 캠페인)이었는지 */
  isNewTouch: boolean
}

let state: TrafficState | null = null

/** 앱 시작 시 한 번 호출. 이후에는 `getTraffic()` 으로 읽는다. */
export function captureTraffic(): TrafficState {
  if (state) return state

  const current = readCurrentTouch()
  const storedSession = read(session, KEY_SESSION)
  const storedFirst = read(local, KEY_FIRST)
  const storedLast = read(local, KEY_LAST)

  /**
   * 이번 세션 값을 새로 쓸지:
   *  - 세션 값이 없으면 (새 방문) — 내부 이동이더라도 direct 로 시작
   *  - 이미 있어도 이번 진입이 직접 유입이 아니면 (같은 탭에서 새 캠페인 링크로 다시 들어옴)
   */
  const fallbackDirect: Touch = {
    channel: 'direct',
    source: '(direct)',
    medium: '(none)',
    landing_page: location.pathname,
    has_utm: false,
    at: new Date().toISOString(),
  }
  const isNewTouch = !storedSession || (!!current && current.channel !== 'direct')
  const sessionTouch = isNewTouch ? (current ?? fallbackDirect) : storedSession!

  const first = storedFirst ?? sessionTouch
  // last non-direct: 직접 유입은 이전 경로를 지우지 않는다
  const last =
    isNewTouch && sessionTouch.channel !== 'direct' ? sessionTouch : (storedLast ?? sessionTouch)

  if (isNewTouch) write(session, KEY_SESSION, sessionTouch)
  if (!storedFirst) write(local, KEY_FIRST, first)
  if (last !== storedLast) write(local, KEY_LAST, last)

  state = { first, last, session: sessionTouch, isNewTouch }
  return state
}

export const getTraffic = () => state ?? captureTraffic()

/* ── 문의 폼 링크에 유입 정보 붙이기 ── */

/**
 * 문의 CTA 링크에 방문자의 유입 정보를 쿼리로 이어 붙인다.
 *
 *  - http(s) 주소일 때만 처리. `mailto:`·`tel:`·`#contact` 같은 앵커·상대 경로는 그대로 둔다.
 *  - 링크에 이미 있는 파라미터는 **덮어쓰지 않는다** (Admin 에서 `?ref=annx-landing` 처럼 넣은 값 우선).
 *  - 값은 last non-direct 유입 기준. utm 이 없던 방문은 referrer 로 추정한 값을 넣는다
 *    (예: 네이버 검색 → utm_source=naver&utm_medium=organic, 직접 유입 → (direct)/(none)).
 */
export function decorateContactHref(href: string): string {
  const raw = href.trim()
  if (!/^https?:\/\//i.test(raw)) return href

  let url: URL
  try {
    url = new URL(raw)
  } catch {
    return href
  }

  const t = getTraffic().last
  const params: Record<string, string | undefined> = {
    utm_source: t.source,
    utm_medium: t.medium,
    utm_campaign: t.campaign,
    utm_term: t.term,
    utm_content: t.content,
    ref: t.ref,
  }
  for (const [k, v] of Object.entries(params)) {
    if (v && !url.searchParams.has(k)) url.searchParams.set(k, v)
  }
  return url.toString()
}
